/**
 * PostHog analytics client for DIKDOC web app.
 *
 * Activated when VITE_POSTHOG_API_KEY env var is set.
 * Captures:
 *   - Page views (auto via history listener)
 *   - Custom events (track)
 *   - User identification
 *   - Feature flags
 */
import posthog from "posthog-js";

const logger = {
  info: (...args: unknown[]) => console.info("[posthog]", ...args),
  warn: (...args: unknown[]) => console.warn("[posthog]", ...args),
  error: (...args: unknown[]) => console.error("[posthog]", ...args),
};

let initialized = false;

export function initPostHog(): void {
  if (initialized) return;
  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY as string | undefined;
  if (!apiKey) {
    logger.info("PostHog disabled (no VITE_POSTHOG_API_KEY)");
    return;
  }

  try {
    posthog.init(apiKey, {
      api_host: (import.meta.env.VITE_POSTHOG_HOST as string) || "https://us.i.posthog.com",
      person_profiles: "identified_only",
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      session_recording: {
        maskAllInputs: true,
        recordCrossOriginIframes: false,
      },
      // Respect DoNotTrack
      respect_dnt: true,
      // Disable in dev mode to avoid noise
      loaded: (ph) => {
        if (import.meta.env.DEV) ph.opt_out_capturing();
      },
    });
    initialized = true;
    logger.info("PostHog initialized");
  } catch (e: any) {
    logger.warn({ err: e.message }, "PostHog init failed");
  }
}

export const analytics = {
  isEnabled: () => initialized,

  /** Identify a user (after login). */
  identify(userId: string, traits?: Record<string, unknown>) {
    if (!initialized) return;
    posthog.identify(userId, traits);
  },

  /** Track a custom event. */
  track(eventName: string, properties?: Record<string, unknown>) {
    if (!initialized) return;
    posthog.capture(eventName, properties);
  },

  /** Set user properties (without sending an event). */
  setPerson(properties: Record<string, unknown>) {
    if (!initialized) return;
    posthog.set_person_properties(properties);
  },

  /** Check a feature flag. */
  isFeatureEnabled(flag: string): boolean {
    if (!initialized) return false;
    return posthog.isFeatureEnabled(flag);
  },

  /** Reset (on logout). */
  reset() {
    if (!initialized) return;
    posthog.reset();
  },
};

/** Domain-specific event helpers — for clinical / ops tracking. */
export const clinicalEvents = {
  patientViewed: (patientId: string) => analytics.track("patient_viewed", { patient_id: patientId }),
  consultationStarted: (encounterId: string) => analytics.track("consultation_started", { encounter_id: encounterId }),
  prescriptionCreated: (drugCount: number) => analytics.track("prescription_created", { drug_count: drugCount }),
  alertAcknowledged: (alertId: string, severity: string) => analytics.track("alert_acknowledged", { alert_id: alertId, severity }),
  socThreatViewed: (kind: string) => analytics.track("soc_threat_viewed", { kind }),
  icd10Suggested: (query: string, topCode: string) => analytics.track("icd10_suggested", { query, top_code: topCode }),
  densityChanged: (density: string) => analytics.track("density_changed", { density }),
  localeChanged: (locale: string) => analytics.track("locale_changed", { locale }),
  cockpitEntered: (cockpit: "clinical" | "operations" | "security") => analytics.track("cockpit_entered", { cockpit }),
};

export default analytics;